import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "flex flex-col gap-4 mt-8" }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { key: 3 }
const _hoisted_6 = { key: 1 }

import {
  SettingCategory,
  useAccountStore,
  useConfigSettingsStore,
  useSelfServicePhysicalMembershipCardStore,
  useAnalyticsStore,
} from '@/store';
import ModalDialog from '../ModalDialog/ModalDialog.vue';
import { ref, computed, watch } from 'vue';
import { addDays, isBefore } from 'date-fns';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import SuccessfullyOrdered from './SuccessfullyOrdered.vue';
import AlreadyOrdered from './AlreadyOrdered.vue';
import OrderError from './OrderError.vue';
import { FormEvents } from '@/enums/form-events.enum';

enum ModalState {
  CanOrder,
  AlreadyOrdered,
  SuccessfullyOrdered,
  Error,
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderPhysicalCardModal',
  props: {
    membershipCardModal: {}
  },
  setup(__props: any, { expose: __expose }) {

const accountStore = useAccountStore();
const configStore = useConfigSettingsStore();
const physicalCardStore = useSelfServicePhysicalMembershipCardStore();
const analyticsStore = useAnalyticsStore();

const showModal = ref<boolean>(false);
const loading = ref<boolean>(false);

const modalState = ref<ModalState>(ModalState.CanOrder);

const props = __props;

watch(
  () => showModal.value,
  (newVal) => {
    if (newVal) {
      const lastRequestDate = accountStore.identityDetails?.membershipCardRequestedDate;
      if (lastRequestDate) {
        physicalCardStore.calcExpectedDeliveryDate(lastRequestDate);
        const canOrderNewCard = !lastRequestDate || isBefore(physicalCardStore.expectedArrivalDate!, new Date());
        if (!canOrderNewCard) {
          modalState.value = ModalState.AlreadyOrdered;
        }
      }
    }
  }
);

const orderNewCardBtn = computed(() => {
  return props.membershipCardModal?.fields?.orderNewCardBtn?.value;
});

const orderNewCardTextContent = computed(() => {
  const orderNewCardText = props.membershipCardModal?.fields?.orderNewCardText?.value;

  let value = replaceBookmark('firstname', orderNewCardText ?? '', accountStore.identityDetails?.firstName ?? '');
  value = replaceBookmark('lastname', value, accountStore.identityDetails?.lastName ?? '');
  const postalAddress = accountStore.identityDetails?.addresses?.find(
    (item) => item.addressType?.toLowerCase() == 'postal'
  );

  if (postalAddress) {
    value = replaceBookmark('addressLine1', value, postalAddress.formattedLong ?? '');
  }

  return { value };
});

__expose({ showModal: () => (showModal.value = true) });

const calculateWaitTime = async () => {
  if (!physicalCardStore.waitTime) {
    const waitTime = (await configStore.getSetting(
      'WaitTimeForPhysicalCard',
      SettingCategory.CommonSettings
    )) as number;
    physicalCardStore.waitTime = waitTime ?? 30;
  }

  const lastRequestDate = accountStore.identityDetails?.membershipCardRequestedDate;
  physicalCardStore.calcExpectedDeliveryDate(lastRequestDate);
  const canOrderNewCard = !lastRequestDate || isBefore(physicalCardStore.expectedArrivalDate!, new Date());

  if (canOrderNewCard) {
    modalState.value = ModalState.CanOrder;
    analyticsStore.pushAnalytics({
      event: FormEvents.START,
      form: {
        name: 'Order physical card',
        stepName: 'FormStarted',
      },
    });
  } else {
    modalState.value = ModalState.AlreadyOrdered;
    analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Order physical card',
        stepName: 'FormStarted',
        formErrorDetail: 'You have already ordered a physical card',
        formErrorType: 'We found an existing request',
      },
    });
  }
};

watch(showModal, calculateWaitTime);

const orderNewPhysicalCard = async () => {
  loading.value = true;
  try {
    const res = await physicalCardStore.orderCard();
    if (!res?.value) throw res;

    accountStore.identityDetails!.membershipCardRequestedDate = new Date();
    calculateWaitTime();
    modalState.value = ModalState.SuccessfullyOrdered;
    analyticsStore.pushAnalytics({
      event: FormEvents.COMPLETE,
      form: {
        name: 'Order physical card',
        stepName: 'FormCompleted',
      },
    });
  } catch (error) {
    modalState.value = ModalState.Error;
    analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Order physical card',
        formErrorDetail: 'We are having trouble processing your request',
        formErrorType: 'Unable to complete order',
      },
    });
    console.error('Unable to order physical card: ', error);
  }
  loading.value = false;
};

const onCancelOrderClick = () => {
  showModal.value = false;
};

const onCloseChildModal = async () => {
  showModal.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalDialog, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showModal).value = $event)),
    closeOnEsc: false,
    showXCloseButton: true,
    size: "md"
  }, {
    default: _withCtx(() => [
      (showModal.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (modalState.value == ModalState.CanOrder)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                  _createVNode(_unref(RichText), { field: orderNewCardTextContent.value }, null, 8, ["field"]),
                  _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("button", {
                      class: "btn btn-primary btn-md",
                      onClick: orderNewPhysicalCard
                    }, _toDisplayString(orderNewCardBtn.value), 1),
                    _createElementVNode("button", {
                      class: "btn btn-secondary btn-md",
                      onClick: onCancelOrderClick
                    }, "Cancel")
                  ])
                ]))
              : _createCommentVNode("", true),
            (modalState.value == ModalState.AlreadyOrdered)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(AlreadyOrdered, {
                    membershipCardModal: _ctx.membershipCardModal,
                    onCloseChildModal: onCloseChildModal
                  }, null, 8, ["membershipCardModal"])
                ]))
              : _createCommentVNode("", true),
            (modalState.value == ModalState.SuccessfullyOrdered)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(SuccessfullyOrdered, {
                    membershipCardModal: _ctx.membershipCardModal,
                    onCloseChildModal: onCloseChildModal
                  }, null, 8, ["membershipCardModal"])
                ]))
              : _createCommentVNode("", true),
            (modalState.value == ModalState.Error)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(OrderError, {
                    membershipCardModal: _ctx.membershipCardModal,
                    onCloseChildModal: onCloseChildModal
                  }, null, 8, ["membershipCardModal"])
                ]))
              : _createCommentVNode("", true)
          ], 64))
        : (_openBlock(), _createElementBlock("div", _hoisted_6))
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})